<template>
  <modal-dialog
    v-bind:content-class="customClass"
    v-bind:title="title"
    v-bind:value="value"
    v-bind:with-previous-button="withPreviousButton"
    v-on:input="onInput"
    v-on:on-previous-button="onPreviousButton"
    with-cross
    max-width="90%"
    width="700px"
  >
    <template v-if="withSelectTeeth" v-slot:before-title>
      <teeth-select-popup
        v-show="isTeethSelectShown"
        v-bind:key="value"
        v-on:selected="onSelectTeeth"
      >
        <template v-slot:actions>
          <collapse-button
            class="picker__buttons-collapse"
            caption="Скрыть"
            v-model="isTeethSelectShown"
          />
        </template>
      </teeth-select-popup>
    </template>
    <template v-slot:header>
      <button
        v-if="isShowSelectedTeethButton"
        class="picker__tooth"
        type="button"
        v-on:click="isTeethSelectShown = true"
      >
        <img src="@/assets/img/tooth.svg" class="picker__tooth__image" alt=">Выберите зуб" />
        Выберите зубы
      </button>
      <slot name="header" />
    </template>
    <form class="picker" v-on:submit.prevent>
      <v-expansion-panels>
        <p
          v-if="priceListName"
          class="picker__title"
        >{{priceListName}}</p>
        <slot
          v-bind="{
            categoriesList,
            emitAddService,
            selectedServices,
          }"
        />
        <template
          v-for="category in categoriesList"
        >
          <accordion-stages-group
            v-if="category.hasStages"
            v-bind:key="category.id"
            v-bind:group="category"
            v-on:select-service="emitAddService"
          />
          <accordion-services-group
            v-else-if="category.isHasServices()"
            v-bind:key="category.id"
            v-bind:group="category"
            v-on:select-service="emitAddService"
          />
        </template>
        <p
          class="picker__title"
          v-if="!isCategoriesPlanTreatmentEmpty"
        >Планы лечения</p>
        <template
          v-for="treatmentCategory in initialTreatmentPlanList"
        >
          <accordion-stages-group
            v-if="treatmentCategory.hasStages"
            v-bind:key="treatmentCategory.id"
            v-bind:group="treatmentCategory"
            v-on:select-service="emitAddService"
          />
          <accordion-services-group
            v-else-if="treatmentCategory.isHasServices()"
            v-bind:key="treatmentCategory.id"
            v-bind:group="treatmentCategory"
            v-on:select-service="emitAddService"
          />
        </template>
      </v-expansion-panels>
    </form>
  </modal-dialog>
</template>

<script>
import ModalDialog from '@/components/ModalDialog.vue';
import AccordionStagesGroup from '@/components/AccordionStagesGroup.vue';
import AccordionServicesGroup from '@/components/AccordionServicesGroup.vue';
import TeethSelectPopup from '@/components/TeethSelectPopup.vue';
import CollapseButton from '@/components/CollapseButton.vue';

/**
 * @description Название события "Назад".
 * @type {string}
 */
const ON_CLICK_PREVIOUS_BUTTON = 'on-previous-button';

/**
 * @description Список выбранных услуг.
 * @param {Object[]} initialList Выбранные услуги.
 * @constructor
 */
function ServiceSelectedList(initialList = []) {
  const list = new Map(initialList.map((item) => [item.id, item]));

  this.count = () => list.size;

  /**
   * @description Добавить услуги в список.
   * @type {Object[]} services
   * @returns {ServiceSelectedList}
   */
  this.add = (...services) => {
    services.forEach((service) => {
      list.set(service.id, service);
    });
    return new this.constructor([...list.values()]);
  };

  /**
   * @description Удалить услуги из списка.
   * @type {Object[]} services
   * @returns {ServiceSelectedList}
   */
  this.remove = (...services) => {
    services.forEach((service) => {
      list.delete(service.id);
    });
    return new this.constructor([...list.values()]);
  };

  /**
   * @description Проверить наличие услуги в списке.
   * @type {Object} service
   * @returns {boolean}
   */
  this.contains = (service) => list.has(service.id);

  /**
   * @description Преобразовать к массиву.
   * @returns {Object[]}
   */
  this.toArray = () => [...list.values()];
}

/**
 * @description Название события добавления услуги.
 * @type {string}
 */
const NAME_EVENT_ADD_SERVICE = 'add-service';

/**
 * @description Название события ввода.
 * @type {string}
 */
const NAME_EVENT_INPUT = 'input';

/**
 * @description Отфильтровать список категорий.
 * @param {Array<Category>} categories
 * @returns {Array<Category>}
 */
function filterCategoryList(categories) {
  return categories.filter((item) => item.isHasServices());
}

/**
 * @description Выбор услуг из прайса.
 * @type {Vue}
 */
export default {
  components: {
    AccordionServicesGroup,
    AccordionStagesGroup,
    ModalDialog,
    TeethSelectPopup,
    CollapseButton,
  },
  provide() {
    return {
      closePopover: () => {},
    };
  },
  computed: {
    /**
     * Категории планов лечения пусты
     *
     * @return {boolean}
     */
    isCategoriesPlanTreatmentEmpty() {
      return this.initialTreatmentPlanList.length <= 0;
    },
    /**
     * @description Определить кастомный набор классов.
     */
    customClass() {
      const positionStartClass = this.positionStart ? 'position-start' : '';
      return [this.contentClass, positionStartClass].join(' ').trim();
    },
    /**
     * @description Показывать кнопку выбора зубов.
     */
    isShowSelectedTeethButton() {
      return this.withSelectTeeth && !this.isTeethSelectShown;
    },
  },
  methods: {
    /**
     * @description Отправить событие добавления услуги.
     * @param {Object} service
     * @returns {void}
     */
    emitAddService(service) {
      const addingService = { ...service };
      let { count } = addingService;
      if ((addingService.dentalParts || []).length === 0 && this.selectedDentalParts.length > 0) {
        addingService.dentalParts = this.selectedDentalParts;
      }
      if ((addingService.teeth || []).length === 0 && this.selectedTeeth.length > 0) {
        count = this.selectedTeeth.length;
        addingService.teeth = this.selectedTeeth;
      }
      if (this.toothGroupSelected) {
        addingService.count = 1;
      } else if (count !== service.count) {
        addingService.count = count;
      }
      this.$emit(NAME_EVENT_ADD_SERVICE, addingService);
    },
    /**
     * Обработать событие нажатия на кнопку "Назад".
     * @returns {void}
     */
    onPreviousButton() {
      this.$emit(ON_CLICK_PREVIOUS_BUTTON, false);
    },
    /**
     * @description Обработать показ окна.
     * @param {boolean} isShown Показывать.
     * @returns {void}
     */
    onInput(isShown) {
      this.$emit(NAME_EVENT_INPUT, isShown);
    },
    /**
     * @description Обработать выбор зубов.
     * @param {number[]} teeth
     * @param {string[]} dentalParts
     * @param {boolean}  toothGroupSelected
     */
    onSelectTeeth(teeth, dentalParts, toothGroupSelected) {
      this.selectedTeeth = teeth;
      this.selectedDentalParts = dentalParts;
      this.toothGroupSelected = toothGroupSelected;
    },
  },
  data() {
    return {
      /**
       * @description Панели с прайсами.
       * @type {Array}
       */
      pricePanels: [],
      /**
       * @description Панели с категориями.
       * @type {Array}
       */
      categoriesPanels: [],
      /**
       * @description Прайс листы.
       * @type {Array<PriceList>}
       */
      priceLists: [],
      /**
       * @description Выбранные услуги.
       * @type {ServiceSelectedList}
       */
      selectedServices: new ServiceSelectedList(),
      /**
       * @description Список категорий
       * @type Array<Category>
       */
      categoriesList: [],
      /**
       * @description Выбранные зубы.
       * @type {Number[]}
       */
      selectedTeeth: [],
      /**
       * @description Зубные части.
       * @type {string}
       */
      selectedDentalParts: [],
      /**
       * @description Показывать форму выбора зубов.
       * @type {boolean}
       */
      isTeethSelectShown: false,
      /**
       * @description Выбрана группа зубов.
       * @type {boolean}
       */
      toothGroupSelected: false,
    };
  },
  name: 'PickerServices',
  props: {
    /**
     * @description Отобразить кнопку "Назад".
     * @type {boolean}
     */
    withPreviousButton: { default: () => false, type: Boolean },
    /**
     * @description Пользовательский класс.
     * @type {String}
     */
    contentClass: { default: '', type: String },
    /**
     * @description Выранивание модального окна по верхнему краю относительно поперечной оси.
     * @type {Boolean}
     */
    positionStart: { default: false, type: Boolean },
    /**
     * @description Список категорий.
     * @type {Array<Category>}
     */
    initialCategoryList: { required: false, type: Array, default: () => [] },
    /**
     * @description Список категорий плана лечения.
     * @type {Array<Category>}
     */
    initialTreatmentPlanList: { required: false, type: Array, default: () => [] },
    /**
     * @description Показывать аккордеон.
     * @type {Boolean}
     */
    shownAccordion: { default: () => true, type: Boolean },
    /**
     * @description Имя прайс-листа
     * @type {String}
     */
    priceListName: { default: () => '', type: String },
    value: { required: true, type: Boolean },
    title: { default: () => '', type: String },
    withSelectTeeth: { default: false, type: Boolean },
  },
  watch: {
    value(newValue) {
      if (newValue) {
        this.panel = undefined;
        this.selectedTeeth = [];
        this.selectedDentalParts = [];
        this.isTeethSelectShown = false;
        this.toothGroupSelected = false;
      }
    },
    initialCategoryList: {
      handler(value) { this.categoriesList = filterCategoryList(value); },
      immediate: true,
    },
  },
};
</script>

<style scoped lang="scss">
@use "@/scss/buttons";

.picker {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 1000px;
  gap: 4px;
  margin: 10px 0;
  height: 100%;
  &__title {
    font-size: 18px;
    max-height: 135px;
    margin: 15px auto 15px !important;
    word-break: break-all;
  }
  &__actions {
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 15px;
    margin: 10px 0 0 0;
  }
  &__buttons {
    column-gap: 9px;
    display: grid;
    grid-template-columns: 180px 180px;
    &-submit {
      @include buttons.btn-blue(100%, 48px, 16px);
    }
    &-submit:disabled {
      background-color: var(--unfocus-color);
    }
    &-cancel {
      @include buttons.btn-transparent(100%, 48px, 16px);
    }
    &-collapse {
      grid-column: 3;
    }
  }

  &__items {
    list-style: none;
    padding: 0;
  }
  &__tooth {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 10px;
    font-weight: 400;

    &__image {
      width: auto;
      height: 30px;
      object-fit: contain;
    }
  }
}
@media screen and (max-width: 480px) {
  .picker {
    &__buttons {
      grid-template-columns: 1fr;
      grid-gap: 10px;
    }
  }
}
</style>
